import { z } from 'zod';
import { BusinessCodeSchema, PhoneSchema } from './identifiers';
import { LocaleSchema, SUPPORTED_LOCALES } from './localized';
import { CurrencyCodeSchema } from './money';
import { InstantSchema, isValidTimeZone } from './time';

/**
 * The deployment's company profile and branding (PLAT-022, PLAT-023, ADR-0027).
 *
 * The product is sold to many real-estate companies, and **each company gets its own deployment**:
 * its own database, keys, storage, domain and provider accounts. Inside one deployment there is
 * therefore exactly one company profile — the identity every screen, printed document and
 * authenticator label uses. It is configuration, not code: changing a company's name, logo or colour
 * never needs a rebuild, and no company's identity is ever written into the product's source.
 *
 * What the profile deliberately does **not** hold:
 *
 * - **Secrets.** No provider credential, token, password, key or bank credential — the schema is
 *   strict, so a request carrying one is refused rather than stored. Secrets are deployment
 *   environment configuration (ADR-0015).
 * - **A tenant identifier.** There is no `tenantId` anywhere: a deployment is one company (ADR-0027).
 * - **Legal entities.** Those are `CORE-ORG` records; a company may operate through several.
 */

/** Labels shown on screens and documents, bounded so a template can lay them out. */
const boundedLabel = (max: number) =>
  z.strictObject({
    ar: z.string().trim().min(1).max(max),
    en: z.string().trim().min(1).max(max),
  });

/**
 * A text the company may supply in one language before the other (white-label identity): each language
 * is independent, at least one is present, and changing one never touches the other. A screen or a
 * document in a language with no value **omits** it — it never borrows the other language's text.
 */
const partialLabel = (max: number) =>
  z
    .strictObject({
      ar: z.string().trim().min(1).max(max).optional(),
      en: z.string().trim().min(1).max(max).optional(),
    })
    .refine((value) => value.ar !== undefined || value.en !== undefined, {
      message: 'LABEL_EMPTY',
    });

/** The trade and short names: required in both languages, because every screen shows one of them. */
export const CompanyNameSchema = boundedLabel(200);
export const CompanyShortNameSchema = boundedLabel(40);
/** An optional name per language (the legal name, before the legal record is confirmed in both). */
export const CompanyOptionalNameSchema = partialLabel(200);
/** Multi-line text per language: an address, a document footer. */
export const CompanyTextSchema = partialLabel(600);
/** A short line under the name on the sign-in screen and in the sidebar, per language. */
export const CompanyTaglineSchema = partialLabel(120);
export type LocalizedText = { ar?: string | undefined; en?: string | undefined };

export const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, { message: 'ISO_3166_ALPHA2' });

export const CompanyIdentifierSchema = z.strictObject({
  /** A stable code chosen by the deployment, e.g. `VAT`, `CHAMBER`. Never a translated label. */
  kind: BusinessCodeSchema,
  value: z.string().trim().min(1).max(60),
});
export type CompanyIdentifier = z.infer<typeof CompanyIdentifierSchema>;

/** `#RRGGBB`. Contrast is validated by the server against every pair in use (THEME-013). */
export const BrandColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'BRAND_COLOR_INVALID' });

/**
 * The rest of the brand palette (white-label identity). Every entry is an optional `#RRGGBB` — never
 * CSS, a URL or code: the schema is strict, so anything else is refused. Absent entries are derived
 * from the primary colour (or the approved defaults). The whole resulting palette must pass every
 * WCAG AA pair the product uses, checked by the server on save and again by the browser before use.
 * Status colours (success, warning, error, information) are never part of it.
 */
export const BRAND_PALETTE_KEYS = [
  'primaryHover',
  'primaryPressed',
  'primarySoft',
  'accent',
  'link',
  'focus',
  'documentHeader',
] as const;
export type BrandPaletteKey = (typeof BRAND_PALETTE_KEYS)[number];
export const BrandPaletteSchema = z.strictObject({
  primaryHover: BrandColorSchema.optional(),
  primaryPressed: BrandColorSchema.optional(),
  primarySoft: BrandColorSchema.optional(),
  accent: BrandColorSchema.optional(),
  link: BrandColorSchema.optional(),
  focus: BrandColorSchema.optional(),
  documentHeader: BrandColorSchema.optional(),
  /** Extra chart series after the primary (chart 2 onwards), in order. */
  chart: z.array(BrandColorSchema).max(5).optional(),
});
export type BrandPalette = z.infer<typeof BrandPaletteSchema>;

export const WebsiteSchema = z
  .string()
  .trim()
  .max(200)
  .regex(/^https:\/\/[^\s/$.?#].[^\s]*$/i, { message: 'HTTPS_URL_EXPECTED' });

export const CompanyEmailSchema = z
  .string()
  .trim()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, { message: 'EMAIL_EXPECTED' });

export const TimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, { message: 'TIME_ZONE_INVALID' });

/** Which documents a signatory signs. `general` = any document not listed separately. */
export const SIGNATORY_DOCUMENTS = [
  'contracts',
  'receipts',
  'statements',
  'invoices',
  'general',
] as const;
export const SignatoryDocumentSchema = z.enum(SIGNATORY_DOCUMENTS);
export type SignatoryDocument = z.infer<typeof SignatoryDocumentSchema>;

/**
 * An authorized signatory as the client names them — never invented. Printing a signature block is a
 * later, approved-wording decision; the profile only records who may sign what.
 */
export const CompanySignatorySchema = z.strictObject({
  name: boundedLabel(120),
  title: boundedLabel(120),
  documents: z.array(SignatoryDocumentSchema).min(1).max(SIGNATORY_DOCUMENTS.length),
});
export type CompanySignatory = z.infer<typeof CompanySignatorySchema>;

/**
 * What the company WhatsApp number is for (ADR-0043). `manualSending` — the company account staff
 * must be signed in to when they send manual reminders; `contactOnly` — a number printed for customers.
 * Neither makes a click-to-chat link send *from* the number: the sender is always the WhatsApp account
 * open on the employee's device. An automatic API sender is a separate provider configuration.
 */
export const WHATSAPP_NUMBER_USES = ['manualSending', 'contactOnly'] as const;
export const WhatsAppNumberUseSchema = z.enum(WHATSAPP_NUMBER_USES);
export type WhatsAppNumberUse = z.infer<typeof WhatsAppNumberUseSchema>;

const profileFields = {
  /**
   * The name on the commercial register. Optional until the client confirms the legal record: the
   * trade name is what screens and documents show, and a document prints the legal name only when it
   * is known and differs.
   */
  legalName: CompanyOptionalNameSchema.optional(),
  /** The trading / display name. */
  tradeName: CompanyNameSchema,
  /** The name on a narrow screen, in the browser tab and in an authenticator application. */
  shortName: CompanyShortNameSchema,
  tagline: CompanyTaglineSchema.optional(),
  commercialRegistration: z.string().trim().min(1).max(60).optional(),
  taxRegistration: z.string().trim().min(1).max(60).optional(),
  otherIdentifiers: z.array(CompanyIdentifierSchema).max(10).default([]),
  /** The registered address (on the commercial register). */
  address: CompanyTextSchema.optional(),
  /** Where correspondence goes, when it differs from the registered address. */
  correspondenceAddress: CompanyTextSchema.optional(),
  country: CountryCodeSchema,
  phone: PhoneSchema.optional(),
  email: CompanyEmailSchema.optional(),
  website: WebsiteSchema.optional(),
  /** The company's WhatsApp number — configuration only, never a product default. */
  whatsappNumber: PhoneSchema.optional(),
  whatsappNumberUse: WhatsAppNumberUseSchema.optional(),
  defaultLocale: LocaleSchema,
  supportedLocales: z.array(LocaleSchema).min(1).max(SUPPORTED_LOCALES.length),
  timeZone: TimeZoneSchema,
  baseCurrency: CurrencyCodeSchema,
  primaryColor: BrandColorSchema.optional(),
  brandPalette: BrandPaletteSchema.optional(),
  /** Printed at the foot of generated documents, in the document's language. */
  documentFooter: CompanyTextSchema.optional(),
  signatories: z.array(CompanySignatorySchema).max(8).default([]),
};

/** The locale rules every profile obeys: no duplicates, and the default is one of the supported. */
function localesAreCoherent(value: {
  defaultLocale: z.infer<typeof LocaleSchema>;
  supportedLocales: readonly z.infer<typeof LocaleSchema>[];
}): boolean {
  return (
    new Set(value.supportedLocales).size === value.supportedLocales.length &&
    value.supportedLocales.includes(value.defaultLocale)
  );
}

/** A use without a number would describe nothing. */
function whatsappIsCoherent(value: {
  whatsappNumber?: string;
  whatsappNumberUse?: string;
}): boolean {
  return !value.whatsappNumberUse || Boolean(value.whatsappNumber);
}

const WHATSAPP_ISSUE = { message: 'WHATSAPP_NUMBER_REQUIRED', path: ['whatsappNumber'] };

const LOCALES_ISSUE = {
  message: 'DEFAULT_LOCALE_NOT_SUPPORTED',
  path: ['supportedLocales'],
};

export const CompanyProfileInputSchema = z
  .strictObject(profileFields)
  .refine(localesAreCoherent, LOCALES_ISSUE)
  .refine(whatsappIsCoherent, WHATSAPP_ISSUE);
export type CompanyProfileInput = z.infer<typeof CompanyProfileInputSchema>;

/** A full replacement of the editable fields, guarded by the version the editor read. */
export const UpdateCompanyProfileSchema = z
  .strictObject({ ...profileFields, expectedVersion: z.number().int().positive() })
  .refine(localesAreCoherent, LOCALES_ISSUE)
  .refine(whatsappIsCoherent, WHATSAPP_ISSUE);
export type UpdateCompanyProfile = z.infer<typeof UpdateCompanyProfileSchema>;

/**
 * Slots the public branding endpoint may serve before sign-in. `logo` is the primary (language-neutral)
 * logo and the fallback for `logoAr` / `logoEn`; each slot has its own history, so replacing the Arabic
 * logo never touches the English one or the primary.
 */
export const BRAND_ASSET_SLOTS = [
  'logo',
  'logoAr',
  'logoEn',
  'compactLogo',
  'favicon',
  'appIcon',
  'signInVisual',
] as const;
export const BrandAssetSlotSchema = z.enum(BRAND_ASSET_SLOTS);
export type BrandAssetSlot = z.infer<typeof BrandAssetSlotSchema>;

/** The language-specific logo slot for a locale; its fallback is the primary `logo`. */
export const LOCALE_LOGO_SLOT = { ar: 'logoAr', en: 'logoEn' } as const satisfies Record<
  z.infer<typeof LocaleSchema>,
  BrandAssetSlot
>;

/**
 * Pixel bounds per slot, checked from the image header on upload (never by resizing: an official
 * logo is stored exactly as supplied — no stretching, cropping or recolouring).
 */
export const ASSET_DIMENSIONS: Readonly<
  Record<BrandAssetSlot | 'stamp', { min: number; max: number; square?: boolean }>
> = {
  logo: { min: 32, max: 4096 },
  logoAr: { min: 32, max: 4096 },
  logoEn: { min: 32, max: 4096 },
  compactLogo: { min: 32, max: 2048 },
  favicon: { min: 16, max: 512, square: true },
  appIcon: { min: 64, max: 1024, square: true },
  signInVisual: { min: 200, max: 4096 },
  stamp: { min: 64, max: 2048 },
};

/**
 * Every slot the profile holds. The **stamp** is private: it is never part of the public branding and
 * is read only by signed-in holders of `company.profile.view` — a company stamp on an open URL is an
 * invitation to forge it.
 */
export const COMPANY_ASSET_SLOTS = [...BRAND_ASSET_SLOTS, 'stamp'] as const;
export const CompanyAssetSlotSchema = z.enum(COMPANY_ASSET_SLOTS);
export type CompanyAssetSlot = z.infer<typeof CompanyAssetSlotSchema>;
export const PRIVATE_ASSET_SLOTS: readonly CompanyAssetSlot[] = ['stamp'];

export const BRAND_ASSET_TYPES = ['image/png', 'image/jpeg'] as const;

/** A reference to the current image in one slot. The bytes are served by the branding endpoint. */
export const BrandAssetRefSchema = z.strictObject({
  assetId: z.string().min(1).max(80),
  contentType: z.enum(BRAND_ASSET_TYPES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  size: z.number().int().positive(),
});
export type BrandAssetRef = z.infer<typeof BrandAssetRefSchema>;

export const CompanyProfileSchema = z.strictObject({
  ...profileFields,
  otherIdentifiers: z.array(CompanyIdentifierSchema).max(10),
  signatories: z.array(CompanySignatorySchema).max(8),
  assets: z.strictObject({
    logo: BrandAssetRefSchema.optional(),
    logoAr: BrandAssetRefSchema.optional(),
    logoEn: BrandAssetRefSchema.optional(),
    compactLogo: BrandAssetRefSchema.optional(),
    favicon: BrandAssetRefSchema.optional(),
    appIcon: BrandAssetRefSchema.optional(),
    signInVisual: BrandAssetRefSchema.optional(),
    stamp: BrandAssetRefSchema.optional(),
  }),
  /** Increments on every change, including a new image. Documents record the version they used. */
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

/** One published brand image as the public endpoint describes it. */
export const PublicBrandAssetSchema = z.strictObject({
  /** Relative to the API origin, versioned by content hash so a browser never shows a stale image. */
  url: z.string().min(1),
  contentType: z.enum(BRAND_ASSET_TYPES),
});

/**
 * What any browser may read **before** sign-in: enough to draw the sign-in screen, the shell and the
 * browser tab in the company's identity. Registration numbers, contact details and the footer are not
 * part of it — they are printed on documents, not broadcast to anonymous callers.
 *
 * `configured: false` means no profile exists yet, and the application renders its neutral product
 * identity. It is never an error.
 */
export const PublicBrandingSchema = z.strictObject({
  configured: z.boolean(),
  /**
   * True on a development, test or staging deployment, which runs fictional demonstration data
   * (ADR-0026, ADR-0035). The sign-in screen states it; a production deployment never does.
   */
  demonstration: z.boolean(),
  /**
   * Present and true only on the self-hosted staging deployment (ADR-0035): every screen then carries
   * a "staging — client review — fictional data" banner.
   */
  staging: z.literal(true).optional(),
  shortName: CompanyShortNameSchema.optional(),
  tradeName: CompanyNameSchema.optional(),
  tagline: CompanyTaglineSchema.optional(),
  defaultLocale: LocaleSchema,
  supportedLocales: z.array(LocaleSchema).min(1),
  /**
   * The timezone the browser displays instants in (ADR-0008): the company's, or the deployment's
   * configured organization timezone before a profile exists. Never assumed by the client.
   */
  timeZone: z.string().min(1).max(64),
  /**
   * The currency amounts are entered in by default — the one every document prints. Absent before a
   * profile exists; a form then takes the currency of the record it prices, never a guessed one.
   */
  baseCurrency: CurrencyCodeSchema.optional(),
  primaryColor: BrandColorSchema.optional(),
  brandPalette: BrandPaletteSchema.optional(),
  assets: z.strictObject({
    logo: PublicBrandAssetSchema.optional(),
    logoAr: PublicBrandAssetSchema.optional(),
    logoEn: PublicBrandAssetSchema.optional(),
    compactLogo: PublicBrandAssetSchema.optional(),
    favicon: PublicBrandAssetSchema.optional(),
    appIcon: PublicBrandAssetSchema.optional(),
    signInVisual: PublicBrandAssetSchema.optional(),
  }),
  version: z.number().int().nonnegative(),
});
export type PublicBranding = z.infer<typeof PublicBrandingSchema>;

/** One entry of the profile's append-only revision history. */
export const CompanyProfileRevisionSchema = z.strictObject({
  version: z.number().int().positive(),
  changedAt: InstantSchema,
  changedBy: z.string().min(1),
  /** The profile exactly as it stood after this change. */
  profile: CompanyProfileSchema,
});
export type CompanyProfileRevision = z.infer<typeof CompanyProfileRevisionSchema>;

export const CompanyProfileRevisionListSchema = z.strictObject({
  items: z.array(CompanyProfileRevisionSchema),
});

/** One stored image in a slot's history — never its bytes. */
export const BrandAssetHistoryItemSchema = z.strictObject({
  assetId: z.string().min(1).max(80),
  slot: CompanyAssetSlotSchema,
  contentType: z.enum(BRAND_ASSET_TYPES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  size: z.number().int().positive(),
  state: z.enum(['active', 'superseded']),
  uploadedAt: InstantSchema,
  uploadedBy: z.string().min(1),
});
export type BrandAssetHistoryItem = z.infer<typeof BrandAssetHistoryItemSchema>;
export const BrandAssetHistorySchema = z.strictObject({
  items: z.array(BrandAssetHistoryItemSchema),
});

/** Remove the current image from a slot; screens and documents fall back to the company's name. */
export const RemoveBrandAssetSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
});
/** Make an earlier image of the same slot current again — "restore the original logo". */
export const RestoreBrandAssetSchema = z.strictObject({
  assetId: z.string().min(1).max(80),
  expectedVersion: z.number().int().positive(),
});

export const COMPANY_AUDIT_ACTIONS = {
  profileCreated: 'company.profile.created',
  profileUpdated: 'company.profile.updated',
  brandAssetUploaded: 'company.brandAsset.uploaded',
  brandAssetRemoved: 'company.brandAsset.removed',
  brandAssetRestored: 'company.brandAsset.restored',
} as const;

/* ------------------------------------------------------------------- completeness */

/**
 * What a missing value would block. `optional` blocks nothing; the others name the moment it is
 * needed. Only the operation that legally depends on a value is ever refused for it — the rest runs.
 */
export const COMPLETENESS_NEEDS = [
  'optional',
  'documentIssuance',
  'financialPosting',
  'productionLaunch',
] as const;
export type CompletenessNeed = (typeof COMPLETENESS_NEEDS)[number];

export const COMPANY_COMPLETENESS_ITEMS = [
  'legalName',
  'commercialRegistration',
  'taxRegistration',
  'address',
  'phone',
  'email',
  'signatories',
  'logo',
  'logoAr',
  'logoEn',
  'compactLogo',
  'favicon',
  'tagline',
  'correspondenceAddress',
  'website',
  'documentFooter',
  'stamp',
] as const;
export type CompanyCompletenessItem = (typeof COMPANY_COMPLETENESS_ITEMS)[number];

const COMPLETENESS_RULES: Readonly<Record<CompanyCompletenessItem, readonly CompletenessNeed[]>> = {
  legalName: ['documentIssuance', 'productionLaunch'],
  commercialRegistration: ['documentIssuance', 'productionLaunch'],
  // A document that charges tax must name the seller's registration; issuing one is refused without it.
  taxRegistration: ['financialPosting', 'documentIssuance', 'productionLaunch'],
  address: ['documentIssuance', 'productionLaunch'],
  phone: ['productionLaunch'],
  email: ['productionLaunch'],
  signatories: ['documentIssuance', 'productionLaunch'],
  logo: ['optional'],
  logoAr: ['optional'],
  logoEn: ['optional'],
  compactLogo: ['optional'],
  favicon: ['optional'],
  tagline: ['optional'],
  correspondenceAddress: ['optional'],
  website: ['optional'],
  documentFooter: ['optional'],
  stamp: ['optional'],
};

export const CompanyCompletenessSchema = z.strictObject({
  items: z.array(
    z.strictObject({
      item: z.enum(COMPANY_COMPLETENESS_ITEMS),
      complete: z.boolean(),
      needs: z.array(z.enum(COMPLETENESS_NEEDS)),
    }),
  ),
});
export type CompanyCompleteness = z.infer<typeof CompanyCompletenessSchema>;

/** The profile fields completeness looks at — a stored profile or an unsaved draft. */
export interface CompletenessInput {
  legalName?: unknown;
  commercialRegistration?: unknown;
  taxRegistration?: unknown;
  address?: unknown;
  phone?: unknown;
  email?: unknown;
  correspondenceAddress?: unknown;
  website?: unknown;
  documentFooter?: unknown;
  tagline?: unknown;
  signatories?: readonly unknown[];
  assets?: {
    logo?: unknown;
    logoAr?: unknown;
    logoEn?: unknown;
    compactLogo?: unknown;
    favicon?: unknown;
    stamp?: unknown;
  };
}

/** A per-language text counts as complete only when both languages are supplied. */
function inBoth(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const label = value as { ar?: unknown; en?: unknown };
  return Boolean(label.ar) && Boolean(label.en);
}

/**
 * Pure: which profile items are present and what each missing one would block. The server uses it
 * for the readiness view; the editor runs it on the unsaved draft so the warning follows the typing.
 */
export function companyCompleteness(profile: CompletenessInput | undefined): CompanyCompleteness {
  const present: Record<CompanyCompletenessItem, boolean> = {
    legalName: inBoth(profile?.legalName),
    commercialRegistration: Boolean(profile?.commercialRegistration),
    taxRegistration: Boolean(profile?.taxRegistration),
    address: inBoth(profile?.address),
    phone: Boolean(profile?.phone),
    email: Boolean(profile?.email),
    signatories: (profile?.signatories?.length ?? 0) > 0,
    // Any logo shows the company graphically; the per-language items say which variants exist.
    logo: Boolean(profile?.assets?.logo ?? profile?.assets?.logoAr ?? profile?.assets?.logoEn),
    logoAr: Boolean(profile?.assets?.logoAr),
    logoEn: Boolean(profile?.assets?.logoEn),
    compactLogo: Boolean(profile?.assets?.compactLogo),
    favicon: Boolean(profile?.assets?.favicon),
    tagline: inBoth(profile?.tagline),
    correspondenceAddress: inBoth(profile?.correspondenceAddress),
    website: Boolean(profile?.website),
    documentFooter: inBoth(profile?.documentFooter),
    stamp: Boolean(profile?.assets?.stamp),
  };
  return {
    items: COMPANY_COMPLETENESS_ITEMS.map((item) => ({
      item,
      complete: present[item],
      needs: [...COMPLETENESS_RULES[item]],
    })),
  };
}

/** Largest brand image accepted. A logo is displayed small; a larger file is a mistake or an attack. */
export const BRAND_ASSET_MAX_BYTES = 512 * 1024;
